/**
 * Everything Nubian V2 — ambient Nile atmosphere
 * Lightweight canvas particles + hero parallax + river draw
 * Pauses when off-screen; respects prefers-reduced-motion
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isCoarse = window.matchMedia('(pointer: coarse)').matches;
  var pointerParallax = !reduceMotion && !isCoarse;

  /* ---------- Gold dust / fiber particles ---------- */
  function createParticleField(canvas, options) {
    if (!canvas || reduceMotion) return null;

    var ctx = canvas.getContext('2d');
    if (!ctx) return null;

    var opts = options || {};
    var density = opts.density || 28;
    var color = opts.color || '184, 134, 47';
    var running = false;
    var raf = 0;
    var particles = [];
    var pointer = { x: 0.5, y: 0.5, active: false };
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      var rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed(rect.width, rect.height);
    }

    function seed(w, h) {
      var count = Math.max(12, Math.floor((w * h) / 28000) + density);
      count = Math.min(count, opts.max || 32);
      particles = [];
      for (var i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.4 + 0.3,
          vx: (Math.random() - 0.5) * 0.12,
          vy: -0.05 - Math.random() * 0.12,
          a: 0.15 + Math.random() * 0.45,
          phase: Math.random() * Math.PI * 2
        });
      }
    }

    function tick() {
      if (!running) return;
      var w = canvas.clientWidth;
      var h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      var px = (pointer.x - 0.5) * (pointer.active ? 8 : 0);
      var py = (pointer.y - 0.5) * (pointer.active ? 6 : 0);

      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.phase += 0.008;
        p.x += p.vx + Math.sin(p.phase) * 0.08 + px * 0.002;
        p.y += p.vy + py * 0.002;

        if (p.y < -4) {
          p.y = h + 4;
          p.x = Math.random() * w;
        }
        if (p.x < -4) p.x = w + 4;
        if (p.x > w + 4) p.x = -4;

        ctx.beginPath();
        ctx.fillStyle = 'rgba(' + color + ',' + p.a + ')';
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    }

    function start() {
      if (running) return;
      running = true;
      tick();
    }

    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    }

    function onPointer(e) {
      if (!pointerParallax) return;
      var rect = canvas.getBoundingClientRect();
      pointer.x = (e.clientX - rect.left) / rect.width;
      pointer.y = (e.clientY - rect.top) / rect.height;
      pointer.active = true;
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });
    if (pointerParallax) {
      canvas.parentElement.addEventListener('pointermove', onPointer, { passive: true });
      canvas.parentElement.addEventListener(
        'pointerleave',
        function () {
          pointer.active = false;
        },
        { passive: true }
      );
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { threshold: 0.05 }
      );
      io.observe(canvas);
    } else {
      start();
    }

    return { start: start, stop: stop, resize: resize };
  }

  /* ---------- Hero parallax layers ---------- */
  function initHeroParallax() {
    var hero = document.querySelector('[data-en-hero]');
    if (!hero || !pointerParallax) return;

    var layers = hero.querySelectorAll('[data-parallax]');
    if (!layers.length) return;

    var rect = null;
    var targetX = 0;
    var targetY = 0;
    var curX = 0;
    var curY = 0;
    var raf = 0;

    function frame() {
      curX += (targetX - curX) * 0.06;
      curY += (targetY - curY) * 0.06;
      layers.forEach(function (layer) {
        var depth = parseFloat(layer.getAttribute('data-parallax')) || 10;
        layer.style.transform =
          'translate3d(' + curX * depth + 'px,' + curY * depth + 'px,0)';
      });
      raf = requestAnimationFrame(frame);
    }

    hero.addEventListener(
      'pointermove',
      function (e) {
        rect = hero.getBoundingClientRect();
        targetX = (e.clientX - rect.left) / rect.width - 0.5;
        targetY = (e.clientY - rect.top) / rect.height - 0.5;
      },
      { passive: true }
    );

    hero.addEventListener(
      'pointerleave',
      function () {
        targetX = 0;
        targetY = 0;
      },
      { passive: true }
    );

    frame();
  }

  /* ---------- Heritage river draw-on-scroll ---------- */
  function initRiverDraw() {
    var river = document.querySelector('[data-en-river]');
    if (!river) return;

    var path = river.querySelector('.en-heritage__flow-path');
    if (!path || reduceMotion) {
      if (path) path.classList.add('is-drawn');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      path.classList.add('is-drawn');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            path.classList.add('is-drawn');
            river.classList.add('is-alive');
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(river);
  }

  /* ---------- Papyrus catalogue detail (prototype PDP) ---------- */
  function initPapyrusShowcase() {
    var root = document.querySelector('[data-en-papyrus]');
    if (!root) return;

    var featureImg = root.querySelector('[data-en-papyrus-feature-img]');
    var featureTitle = root.querySelector('[data-en-papyrus-feature-title]');
    var featureText = root.querySelector('[data-en-papyrus-feature-text]');
    var openBtns = root.querySelectorAll('[data-en-papyrus-open]');
    var dialog = root.querySelector('[data-en-papyrus-lightbox]');
    var closeBtns = root.querySelectorAll('[data-en-papyrus-close]');
    var closeLinks = root.querySelectorAll('[data-en-papyrus-close-link]');
    var lightImg = root.querySelector('[data-en-papyrus-light-img]');
    var detailTitle = root.querySelector('[data-en-papyrus-detail-title]');
    var detailText = root.querySelector('[data-en-papyrus-detail-text]');
    var lastFocus = null;

    function syncFeature(src, alt, title, text) {
      if (featureImg && src) {
        featureImg.src = src;
        featureImg.alt = alt || '';
      }
      if (featureTitle && title) featureTitle.textContent = title;
      if (featureText && text) featureText.textContent = text;
    }

    function openDetail(src, alt, title, text) {
      if (!dialog || !lightImg) return;
      lastFocus = document.activeElement;
      lightImg.src = src || '';
      lightImg.alt = alt || '';
      if (detailTitle) detailTitle.textContent = title || '';
      if (detailText) detailText.textContent = text || '';
      syncFeature(src, alt, title, text);
      dialog.hidden = false;
      document.body.style.overflow = 'hidden';
      var closer = dialog.querySelector('[data-en-papyrus-close]');
      if (closer) closer.focus();
    }

    function closeDetail() {
      if (!dialog) return;
      dialog.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    openBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        openDetail(
          btn.getAttribute('data-full-src'),
          btn.getAttribute('data-alt'),
          btn.getAttribute('data-title'),
          btn.getAttribute('data-text')
        );
      });
    });

    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', closeDetail);
    });

    closeLinks.forEach(function (link) {
      link.addEventListener('click', function () {
        closeDetail();
      });
    });

    if (dialog) {
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) closeDetail();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dialog && !dialog.hidden) closeDetail();
    });
  }

  function boot() {
    document.querySelectorAll('[data-en-particles]').forEach(function (canvas) {
      var density = parseInt(canvas.getAttribute('data-density') || '18', 10);
      createParticleField(canvas, { density: Math.min(density, 22), max: 28 });
    });
    initHeroParallax();
    initRiverDraw();
    initPapyrusShowcase();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
